import { Box, Button, Collapse, Divider, Flex, FormLabel } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikCheckbox, FormikSelect } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import RadioInput from 'components/RadioCard';
import Title from 'components/Title';
import { roundingOptions, roundingIncrementOptions } from 'containers/CompanySettings/shared/options';
import { useFetchSettings } from 'hooks/companySettings/useFetchSettings';
import {
  useSaveTimeRoundingSettings,
  ISaveTimeRoundingSettings,
} from 'hooks/companySettings/useSaveTimeRoundingSettings';

import { initialTimeRoundingData, timeRoundingSettingsValidation } from './schema';

const TimeRounding: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const [clockInRoundingOpt, setClockInRoundingOpt] = useState<any>();
  const [clockOutRoundingOpt, setClockOutRoundingOpt] = useState<any>();
  const [formData, setFormData] = useState<any>(null);

  const { data, status, refetch } = useFetchSettings('time_rounding');
  const { status: saveTimeRoundingStatus, mutateAsync } = useSaveTimeRoundingSettings();

  useEffect(
    () => {
      if (status === 'success') {
        setClockInRoundingOpt(data?.payload.clock_in.rounding_direction);
        setClockOutRoundingOpt(data?.payload.clock_out.rounding_direction);
        const pformData = {
          clock_in_rounding_direction: data?.payload?.clock_in?.rounding_direction,
          clock_in_rounding_increment: data?.payload?.clock_in?.rounding_increment
            ? roundingIncrementOptions.find((item: any) => item.value === data?.payload?.clock_in?.rounding_increment)
            : 0,
          clock_out_rounding_direction: data?.payload?.clock_out?.rounding_direction,
          clock_out_rounding_increment: data?.payload?.clock_out?.rounding_increment
            ? roundingIncrementOptions.find((item: any) => item.value === data?.payload?.clock_out?.rounding_increment)
            : 0,
          round_clock_in: data?.payload?.round_clock_in ? ['1'] : [],
          round_clock_out: data?.payload?.round_clock_out ? ['1'] : [],
        };
        setFormData(pformData);
      }
    }, // eslint-disable-next-line
    [status]
  );

  const handleFormSubmit = async (values: typeof initialTimeRoundingData) => {
    const postData: ISaveTimeRoundingSettings = {
      round_clock_in: values?.round_clock_in.length ? true : false,
      round_clock_out: values?.round_clock_out.length ? true : false,
      clock_in: {
        rounding_direction: clockInRoundingOpt,
        rounding_increment:
          values?.round_clock_in.length && values?.clock_in_rounding_increment
            ? values?.clock_in_rounding_increment.value
            : 0,
      },
      clock_out: {
        rounding_direction: clockOutRoundingOpt,
        rounding_increment:
          values?.round_clock_out.length && values?.clock_out_rounding_increment
            ? values?.clock_out_rounding_increment.value
            : 0,
      },
    };
    mutateAsync(postData).then(() => {
      refetch();
    });
  };

  if (status === 'loading' || !formData) return <LogoLoader />;

  return (
    <Box>
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton={true}>{t('companySettings:timeRoundingSettings')}</Title>
      </Box>
      <Box mt="26px">
        <Formik
          initialValues={formData}
          validationSchema={timeRoundingSettingsValidation}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {({ values }) => (
            <Form noValidate>
              <Box>
                <Box mb={6}>
                  <FormikCheckbox
                    options={[{ label: t('companySettings:roundClockinTimes'), value: '1' }]}
                    name="round_clock_in"
                    formControlProps={{ marginBottom: '0' }}
                  />
                </Box>
                <Collapse in={values.round_clock_in.length > 0} unmountOnExit={true} animateOpacity>
                  <Flex direction="column">
                    <FormLabel fontSize="sm">{t('companySettings:roundingDirection')}</FormLabel>
                    <RadioInput
                      variant="secondary"
                      options={roundingOptions}
                      name="clock_in_rounding_direction"
                      defaultValue={clockInRoundingOpt}
                      handleChange={setClockInRoundingOpt}
                    ></RadioInput>
                    <Box mt={4} maxWidth="390px">
                      <FormikSelect
                        name="clock_in_rounding_increment"
                        label={t('companySettings:roundingIncrement')}
                        options={roundingIncrementOptions}
                        formControlProps={{ isRequired: true }}
                        selectProps={{
                          placeholder: t('companySettings:selectIncrement'),
                          isClearable: false,
                        }}
                      />
                    </Box>
                  </Flex>
                </Collapse>
              </Box>
              <Divider variant="dashed" mb={6} />
              <Box>
                <Box mb={6}>
                  <FormikCheckbox
                    options={[{ label: t('companySettings:roundClockOutTimes'), value: '1' }]}
                    name="round_clock_out"
                    formControlProps={{ marginBottom: '0' }}
                  />
                </Box>
                <Collapse in={values.round_clock_out.length > 0} unmountOnExit={true} animateOpacity>
                  <Flex direction="column">
                    <FormLabel fontSize="sm">{t('companySettings:roundingDirection')}</FormLabel>
                    <RadioInput
                      variant="secondary"
                      options={roundingOptions}
                      name="clock_out_rounding_direction"
                      defaultValue={clockOutRoundingOpt}
                      handleChange={setClockOutRoundingOpt}
                    ></RadioInput>
                    <Box mt={4} maxWidth="390px">
                      <FormikSelect
                        name="clock_out_rounding_increment"
                        label={t('companySettings:roundingDirection')}
                        options={roundingIncrementOptions}
                        formControlProps={{ isRequired: true }}
                        selectProps={{
                          placeholder: t('companySettings:selectIncrement'),
                          isClearable: false,
                        }}
                      />
                    </Box>
                  </Flex>
                </Collapse>
              </Box>
              <Button
                type="submit"
                colorScheme="secondary"
                minWidth="100px"
                isLoading={saveTimeRoundingStatus === 'loading'}
                mt={6}
              >
                {t('common:save')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};
export default TimeRounding;
